import * as DialogPrimitive from "@radix-ui/react-dialog";
import { usePostHog } from "@posthog/react";
import { X } from "lucide-react";
import { useEffect } from "react";

import {
  BILLING_PERIOD_QUERY_PARAM,
  type BillingPeriod,
  isPlusPurchaseEnabled,
  PLUS_UPGRADE_QUERY_PARAM,
  UPGRADE_COPY,
  type UpgradeReason,
} from "@sill/shared/components/billing/pricing-copy";

import { FEATURE_PLUS, useEntitlementsStore } from "../../stores/entitlements";
import { PricingPanel } from "./pricing-panel";

export interface UpgradeSheetProps {
  open: boolean;
  onClose: () => void;
  reason: UpgradeReason;
  initialBillingPeriod?: BillingPeriod;
}

// UpgradeSheet renders as a full-page modal (per the 2026-05-17 pricing-page
// redesign): viewport-sized Radix Dialog, X close in the top-right, and a
// custom two-card <PricingPanel> in place of Clerk's hosted <PricingTable>.
// The custom panel owns its own CTAs and wires them to Clerk Billing's
// <CheckoutButton> — we control look + tracking, Clerk owns the checkout
// drawer that opens on click.
//
// The `reason` prop still drives a contextual title above the panel so
// triggered surfaces (chat_quota, custom_alert, …) keep their
// existing analytics + UX framing — only the chrome is new.
//
// Emits `sill_plus.upgrade_sheet_opened` whenever `open` flips true (per
// spec §7.3). The effect runs unconditionally before the closed early-out
// so reason transitions within the same surface stay attributable.
export function UpgradeSheet({
  open,
  onClose,
  reason,
  initialBillingPeriod = "annual",
}: UpgradeSheetProps) {
  const copy = UPGRADE_COPY[reason];
  const posthog = usePostHog();
  // Read entitlements directly from the store rather than via useEntitlements,
  // which would require a Clerk getToken resolver and force every test that
  // transitively renders UpgradeSheet to mock <ClerkProvider>. By the time the
  // sheet opens, other surfaces (App boot, Settings, sidebar badge) have
  // already loaded the store — the worst case here is `data === null` which
  // we treat as "not Plus" and show the upsell CTA as usual.
  const plusStatus = useEntitlementsStore((s) => s.data?.[FEATURE_PLUS]);
  const isPlusActive =
    plusStatus?.granted === true &&
    (plusStatus.held_by === "self" ||
      plusStatus.held_by === "household_member");
  const redirectUrl =
    typeof window !== "undefined" ? checkoutReturnUrl() : "/";

  const purchaseEnabled = isPlusPurchaseEnabled();
  useEffect(() => {
    if (open) {
      posthog?.capture("sill_plus.upgrade_sheet_opened", {
        billing_period: initialBillingPeriod,
        reason,
        purchase_enabled: purchaseEnabled,
      });
    }
  }, [open, reason, posthog, purchaseEnabled, initialBillingPeriod]);

  if (!open) return null;

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm" />
        <DialogPrimitive.Content
          className="fixed inset-0 z-50 overflow-y-auto bg-background"
        >
          {/* Title + description are visually-hidden — the PricingPanel hero
              already states the offer; the Radix Title is a a11y requirement
              and shouldn't double up on the visible copy. The contextual
              reason copy renders as an eyebrow above the panel hero so
              triggered surfaces keep their framing. */}
          <DialogPrimitive.Title className="sr-only">
            {copy.title}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            {copy.subtitle}
          </DialogPrimitive.Description>

          <DialogPrimitive.Close
            data-slot="interactive-control"
            data-testid="upgrade-sheet-close"
            className="fixed right-6 top-6 z-50 inline-flex h-10 w-10 items-center justify-center rounded-full bg-card/80 text-muted-foreground shadow-sm backdrop-blur transition-colors hover:bg-card hover:text-foreground"
            aria-label="Close pricing"
          >
            <X className="h-4 w-4" />
          </DialogPrimitive.Close>

          <div className="px-6 pt-10 sm:pt-12">
            <ReasonEyebrow
              title={copy.title}
              subtitle={copy.subtitle}
              reason={reason}
            />
            <PricingPanel
              isPlusActive={isPlusActive}
              onFreeCta={onClose}
              freeCtaLabel="Continue free"
              newSubscriptionRedirectUrl={redirectUrl}
              // Clerk awaits the child CTA's onClick before opening checkout;
              // closing here removes Radix's aria-hidden trap first.
              onCheckoutStart={(period) => {
                posthog?.capture("sill_plus.checkout_clicked", {
                  billing_period: period,
                  reason,
                  purchase_enabled: purchaseEnabled,
                });
                onClose();
              }}
              initialBillingPeriod={initialBillingPeriod}
              density="compact"
            />
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function checkoutReturnUrl(): string {
  const url = new URL(window.location.href);
  url.searchParams.delete(PLUS_UPGRADE_QUERY_PARAM);
  url.searchParams.delete(BILLING_PERIOD_QUERY_PARAM);
  url.searchParams.set("checkout_returned", "1");
  return `${url.pathname}${url.search}${url.hash}`;
}

// Only render the contextual eyebrow for non-default reasons. The generic
// `pricing_page` entry-point lets the hero speak for itself; the gated
// surfaces (chat_quota, custom_alert, rate_cadence_daily) need
// the "you hit X, here's why Plus" framing.
function ReasonEyebrow({
  title,
  subtitle,
  reason,
}: {
  title: string;
  subtitle: string;
  reason: UpgradeReason;
}) {
  if (reason === "pricing_page") return null;
  return (
    <div
      data-testid="upgrade-sheet-reason-eyebrow"
      className="mx-auto mb-6 max-w-2xl rounded-2xl bg-card/60 px-5 py-4 text-center"
    >
      <p className="text-sm font-semibold text-foreground">{title}</p>
      <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
    </div>
  );
}
